import { useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Trophy, ArrowLeft, Mail } from "lucide-react"
import { useForgotPassword } from "@/graphql/hooks/useAuth"
import Layout from "@/components/Layout/Layout"

const ForgotPassword = () => {
  const [email, setEmail] = useState("")
  const [emailSent, setEmailSent] = useState(false)
  const [message, setMessage] = useState("")
  const { forgotPassword, loading } = useForgotPassword()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const result = await forgotPassword(email)

      if (result?.status.success) {
        setEmailSent(true)
        setMessage(result?.status.name)
      }
    } catch (error) {
      console.error("Error sending password recovery email:", error)
    }
  }

  if (emailSent) {
    return (
      <Layout>
        <div className="my-20 flex items-center justify-center bg-gradient-background px-4">
          <Card className="w-full max-w-md bg-gradient-card border-border shadow-elevation">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-secondary rounded-xl flex items-center justify-center shadow-secondary">
                  <Mail className="w-6 h-6 text-white" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold">Correo Enviado</CardTitle>
              <CardDescription>{message}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground text-center">
                  Si no recibes el correo en unos minutos, revisa tu carpeta de spam.
                </div>

                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Volver al inicio de sesión
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="flex items-center justify-center bg-gradient-background px-4 my-20">
        <Card className="w-full max-w-md bg-gradient-card border-border shadow-elevation">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
                <Trophy className="w-6 h-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Recuperar Contraseña</CardTitle>
            <CardDescription>
              Ingresa tu correo electrónico o código de jugador y te enviaremos instrucciones para
              restablecer tu contraseña
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Identificación</Label>
                <Input
                  id="email"
                  type="text"
                  placeholder="tu@email.com o código de jugador"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-muted/50 border-border focus:border-primary"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-primary shadow-glow hover:shadow-glow/80 transition-all duration-300"
                disabled={loading}
              >
                {loading ? "Enviando..." : "Enviar Instrucciones"}
              </Button>

              <Link to="/login">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al inicio de sesión
                </Button>
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}

export default ForgotPassword
